import { IPreloadBridgeClient, PreloadStatusCallback } from './IPreloadBridgeClient';
import {
    PreloadState,
    PreloadStatusMessage,
    PreloadBridgeMessageType,
    GetPreloadStatusMessage,
    StartPreloadMessage,
    ForceRefreshPreloadMessage,
    EnsurePreloadBundlesMessage,
    ActivateForSchoolMessage
} from '../../../../serviceworker/src/preload/PreloadBridgeMessages';

const swc = ((navigator as Navigator).serviceWorker as ServiceWorkerContainer);

/**
 * Progressive Web App implementation of the Preload Bridge Client.
 * Communicates with the Preload Bridge Worker via postMessage.
 */
export default class PWAPreloadBridgeClient implements IPreloadBridgeClient {
    private callbacks: Set<PreloadStatusCallback> = new Set();
    private currentStatus: PreloadStatusMessage | null = null;
    private currentState: PreloadState = 'idle';

    constructor() {
        this.initHandlers();
    }

    /**
     * Initialize message handlers for service worker responses
     */
    private initHandlers(): void {
        swc.addEventListener('message', (event) => {
            if (!event.data || event.data.type !== PreloadBridgeMessageType.status) {
                return; // Not our message
            }

            const status = event.data as PreloadStatusMessage;
            this.handleStatusUpdate(status);
        });

        // Request initial status when service worker is ready
        if (swc.controller) {
            this.requestStatus();
        } else {
            swc.ready.then(() => {
                this.requestStatus();
            });
        }
    }

    /**
     * Handle a status update from the service worker
     */
    private handleStatusUpdate(status: PreloadStatusMessage): void {
        this.currentStatus = status;
        this.currentState = status.state;

        // Notify all callbacks
        for (const callback of this.callbacks) {
            try {
                callback(status);
            } catch (error) {
                console.error('Error in preload status callback:', error);
            }
        }
    }

    /**
     * Register a callback to receive preload status updates
     */
    public onStatusUpdate(callback: PreloadStatusCallback): void {
        this.callbacks.add(callback);

        // Immediately call with current status if available
        if (this.currentStatus) {
            try {
                callback(this.currentStatus);
            } catch (error) {
                console.error('Error in preload status callback:', error);
            }
        }
    }

    /**
     * Remove a status update callback
     */
    public offStatusUpdate(callback: PreloadStatusCallback): void {
        this.callbacks.delete(callback);
    }

    /**
     * Get the current preload state
     */
    public getState(): PreloadState {
        return this.currentState;
    }

    /**
     * Get the current status (last received status message)
     */
    public getStatus(): PreloadStatusMessage | null {
        return this.currentStatus;
    }

    /**
     * Request the service worker to start/resume preloading
     */
    public startPreload(): void {
        if (!swc.controller) {
            console.warn('PWAPreloadBridgeClient: No service worker controller available');
            return;
        }

        swc.controller.postMessage(new StartPreloadMessage());
    }

    /**
     * Request the service worker to force refresh all bundles
     */
    public forceRefresh(): void {
        if (!swc.controller) {
            console.warn('PWAPreloadBridgeClient: No service worker controller available');
            return;
        }

        swc.controller.postMessage(new ForceRefreshPreloadMessage());
    }

    /**
     * Request the service worker to ensure one or more optional bundles.
     * Fire-and-forget — the terminal result arrives on the shared status
     * callback chain, tagged with `ensuredBundles: [...]`.
     */
    public ensureBundles(bundleNames: string[]): void {
        if (!bundleNames || bundleNames.length === 0) {
            return;
        }
        if (!swc.controller) {
            console.warn('PWAPreloadBridgeClient: No service worker controller available');
            return;
        }

        swc.controller.postMessage(new EnsurePreloadBundlesMessage(bundleNames));
    }

    /**
     * Activate preload for a Google Admin-provisioned school code.
     * The code is pre-normalized to a number (leading zeros stripped).
     */
    public activateForSchool(schoolCode: number): void {
        if (!swc.controller) {
            console.warn('PWAPreloadBridgeClient: No service worker controller available');
            return;
        }

        swc.controller.postMessage(new ActivateForSchoolMessage(schoolCode));
    }

    /**
     * Request current status from service worker
     */
    public requestStatus(): void {
        if (!swc.controller) {
            console.warn('PWAPreloadBridgeClient: No service worker controller available');
            return;
        }

        swc.controller.postMessage(new GetPreloadStatusMessage());
    }
}
